/**
 * Undo / redo for the flow map editor: two plain stacks of documents.
 *
 * Pure and React-free; useFlowHistory.ts wraps it in a hook. The component
 * records the document as it was BEFORE each committed edit. Undo swaps the
 * current document for the newest recorded one and parks the current one on
 * the redo stack; redo does the reverse. A new edit after an undo drops the
 * redo stack, as in every editor.
 *
 * Documents are immutable and an edit shares every untouched node with the
 * previous version, so a hundred snapshots cost little more than what changed.
 */

/** How many steps back (and forward) the editor keeps. */
export const FLOW_HISTORY_LIMIT = 100;

export interface FlowHistory<T> {
  /** Oldest first: the last entry is what undo brings back. */
  readonly past: readonly T[];
  /** Nearest first: the first entry is what redo brings back. */
  readonly future: readonly T[];
}

/**
 * - record: an edit was committed; `before` is the document it replaced.
 * - undo: step back; `current` (the document on screen) becomes redoable.
 * - redo: step forward; `current` becomes undoable again.
 * - clear: forget everything (the host replaced the document from outside).
 */
export type FlowHistoryAction<T> =
  | { type: "record"; before: T }
  | { type: "undo"; current: T }
  | { type: "redo"; current: T }
  | { type: "clear" };

const EMPTY: FlowHistory<never> = Object.freeze({ past: Object.freeze([]), future: Object.freeze([]) });

export function emptyHistory<T>(): FlowHistory<T> {
  return EMPTY;
}

/** The document undo would bring back, if any. */
export function undoTarget<T>(h: FlowHistory<T>): T | undefined {
  return h.past[h.past.length - 1];
}

/** The document redo would bring back, if any. */
export function redoTarget<T>(h: FlowHistory<T>): T | undefined {
  return h.future[0];
}

/**
 * The next history. Returns the SAME object when the action changes nothing
 * (an undo with nothing to undo, a repeated record), so a caller can skip a
 * render. Both stacks are capped at `limit`: the oldest undo step and the
 * farthest redo step fall off first.
 */
export function historyReducer<T>(h: FlowHistory<T>, action: FlowHistoryAction<T>, limit: number = FLOW_HISTORY_LIMIT): FlowHistory<T> {
  const max = Number.isFinite(limit) ? Math.max(1, Math.floor(limit)) : FLOW_HISTORY_LIMIT;
  switch (action.type) {
    case "record":
      if (h.future.length === 0 && h.past[h.past.length - 1] === action.before) return h;
      return { past: [...h.past, action.before].slice(-max), future: [] };
    case "undo":
      if (h.past.length === 0) return h;
      return { past: h.past.slice(0, -1), future: [action.current, ...h.future].slice(0, max) };
    case "redo":
      if (h.future.length === 0) return h;
      return { past: [...h.past, action.current].slice(-max), future: h.future.slice(1) };
    case "clear":
      return h.past.length === 0 && h.future.length === 0 ? h : emptyHistory<T>();
  }
}
